import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ITest } from '../../../../data/types';
import { IRankChange } from '../types';

interface UseRankChangesOptions {
  /** Debounce time in ms before processing rank changes */
  debounceMs?: number;
}

interface UseRankChangesResult {
  /** Map of athlete ID to their rank change info */
  rankChanges: Map<string, IRankChange>;
  /** Whether any ranks changed in the last update */
  hasChanges: boolean;
  /** Clear all tracked rank changes */
  clearChanges: () => void;
}

/**
 * Hook to track rank changes between scoreboard updates.
 * Useful for triggering animations when athletes move up/down in rankings.
 *
 * @param currentList - The current sorted list of tests (ranked)
 * @param options - Configuration options
 * @returns Rank change information for each athlete
 */
export const useRankChanges = (
  currentList: ITest[],
  options: UseRankChangesOptions = {}
): UseRankChangesResult => {
  const { debounceMs = 100 } = options;

  const [rankChanges, setRankChanges] = useState<Map<string, IRankChange>>(
    new Map()
  );
  const [hasChanges, setHasChanges] = useState(false);

  // Store previous rankings by athlete ID
  const previousRanksRef = useRef<Map<string, number>>(new Map());
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Derive a stable key from the list to avoid re-running on every array reference change
  const listKey = useMemo(
    () => currentList.map((t) => t.athlete?.id ?? t.id).join(','),
    [currentList]
  );

  useEffect(() => {
    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const previousRanks = previousRanksRef.current;
      const newRankChanges = new Map<string, IRankChange>();
      let changeDetected = false;

      // Build current rankings and detect changes
      currentList.forEach((test, index) => {
        const athleteId = test.athlete?.id;
        if (!athleteId) return;

        const currentRank = index + 1;
        const previousRank = previousRanks.get(athleteId) ?? null;

        let direction: IRankChange['direction'] = 'same';

        if (previousRank === null) {
          direction = 'new';
          changeDetected = true;
        } else if (currentRank < previousRank) {
          direction = 'up';
          changeDetected = true;
        } else if (currentRank > previousRank) {
          direction = 'down';
          changeDetected = true;
        }

        newRankChanges.set(athleteId, {
          athleteId,
          previousRank,
          currentRank,
          direction,
        });
      });

      // Update state
      setRankChanges(newRankChanges);
      setHasChanges(changeDetected);

      // Update previous ranks for next comparison
      const newPreviousRanks = new Map<string, number>();
      currentList.forEach((test, index) => {
        const athleteId = test.athlete?.id;
        if (athleteId) {
          newPreviousRanks.set(athleteId, index + 1);
        }
      });
      previousRanksRef.current = newPreviousRanks;
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey, debounceMs]);

  const clearChanges = useCallback((): void => {
    setRankChanges(new Map());
    setHasChanges(false);
  }, []);

  return {
    rankChanges,
    hasChanges,
    clearChanges,
  };
};

/**
 * Helper to get rank change for a specific athlete
 */
export const getRankChangeForAthlete = (
  rankChanges: Map<string, IRankChange>,
  athleteId: string
): IRankChange | undefined => {
  return rankChanges.get(athleteId);
};

/**
 * Helper to check if an athlete moved up in rank
 */
export const didAthleteRankUp = (
  rankChanges: Map<string, IRankChange>,
  athleteId: string
): boolean => {
  const change = rankChanges.get(athleteId);
  return change?.direction === 'up';
};

/**
 * Helper to check if an athlete is new to the leaderboard
 */
export const isNewToLeaderboard = (
  rankChanges: Map<string, IRankChange>,
  athleteId: string
): boolean => {
  const change = rankChanges.get(athleteId);
  return change?.direction === 'new';
};
